import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  useTheme,
  Skeleton,
  Stack,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import {
  Refresh,
  People,
  Business,
  TrendingUp,
  Analytics,
  AutoAwesome,
  Settings,
  Download
} from '@mui/icons-material';

// Import components
import StatCard from '../dashboard/components/StatCard';
import ChartCard from '../dashboard/components/ChartCard';
import RecentActivityCard from '../dashboard/components/RecentActivityCard';

// Import existing admin components
import UserManagement from './UserManagement';
import PropertyAnalytics from './PropertyAnalytics';
import AIInsights from './components/AIInsights';

// Services
import apiClient from '../../services/apiClient';
import adminService from '../../services/adminService';

const TabPanel = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ py: 3 }}>
    {value === index && children}
  </Box>
);

const EnhancedAdminDashboard = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dashboardMetrics, setDashboardMetrics] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [adminData, propertiesResponse] = await Promise.all([
        adminService.getDashboardMetrics().catch(() => null),
        apiClient.get('/listings', { params: { limit: 10000 } }).catch(() => ({ data: [] }))
      ]);

      const properties = propertiesResponse.data?.data || propertiesResponse.data || [];
      
      setDashboardMetrics({
        totalUsers: adminData?.totalUsers || 0,
        activeUsers: adminData?.activeUsers || 0,
        totalProperties: properties.length || 0,
        savedProperties: adminData?.savedProperties || 0,
        revenueGrowth: 23.5,
        platformHealth: 95,
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardMetrics(getMockMetrics());
    } finally {
      setLoading(false);
    }
  };

  const getMockMetrics = () => ({
    totalUsers: 1523,
    activeUsers: 892,
    totalProperties: 12547,
    savedProperties: 3421,
    revenueGrowth: 23.5,
    platformHealth: 95,
  });

  const mockChartData = {
    userGrowth: [
      { month: 'Jan', users: 1200, properties: 8500 },
      { month: 'Feb', users: 1350, properties: 9200 },
      { month: 'Mar', users: 1420, properties: 10100 },
      { month: 'Apr', users: 1480, properties: 11200 },
      { month: 'May', users: 1510, properties: 12000 },
      { month: 'Jun', users: 1523, properties: 12547 },
    ],
    revenueData: [
      { month: 'Jan', revenue: 45000 },
      { month: 'Feb', revenue: 52000 },
      { month: 'Mar', revenue: 48000 },
      { month: 'Apr', revenue: 61000 },
      { month: 'May', revenue: 55000 },
      { month: 'Jun', revenue: 67000 },
    ],
  };

  const data = dashboardMetrics || getMockMetrics();

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="h4" fontWeight="700">
                Admin Dashboard
              </Typography>
              <Chip 
                label="Administrator" 
                color="error" 
                size="small" 
                icon={<Settings />}
              />
            </Stack>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Platform analytics and management center
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchDashboardData}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
            >
              Export Report
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{
            '& .MuiTab-root': {
              minWidth: 120,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
            }
          }}
        >
          <Tab label="Overview" icon={<Analytics />} iconPosition="start" />
          <Tab label="Users" icon={<People />} iconPosition="start" />
          <Tab label="Properties" icon={<Business />} iconPosition="start" />
          <Tab label="AI Insights" icon={<AutoAwesome />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Total Users"
              value={data.totalUsers?.toLocaleString() || '0'}
              icon={People}
              change={12.5}
              changeType="increase"
              color="primary"
              subtitle="Registered accounts"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Active Users"
              value={data.activeUsers?.toLocaleString() || '0'}
              icon={TrendingUp}
              change={8.3}
              changeType="increase"
              color="success"
              subtitle="Last 30 days"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Total Properties"
              value={data.totalProperties?.toLocaleString() || '0'}
              icon={Business}
              change={15.2}
              changeType="increase"
              color="info"
              subtitle="In database"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Saved Properties"
              value={data.savedProperties?.toLocaleString() || '0'}
              icon={TrendingUp}
              change={23.1}
              changeType="increase"
              color="warning"
              subtitle="User favorites"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Revenue Growth"
              value={`${data.revenueGrowth}%`}
              icon={TrendingUp}
              change={5.4}
              changeType="increase"
              color="success"
              subtitle="Month over month"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Platform Health"
              value={`${data.platformHealth}%`}
              icon={Analytics}
              change={2.1}
              changeType="increase"
              color="primary"
              subtitle="System status"
            />
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} lg={8}>
            <ChartCard
              title="Platform Growth"
              subtitle="Users and properties over time"
              type="line"
              data={mockChartData.userGrowth}
              dataKey={['users', 'properties']}
              xAxisKey="month"
              height={350}
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <ChartCard
              title="Revenue Trend"
              subtitle="Monthly revenue"
              type="bar"
              data={mockChartData.revenueData}
              dataKey="revenue"
              xAxisKey="month"
              height={350}
            />
          </Grid>
        </Grid>

        {/* Recent Activity */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <RecentActivityCard 
              activities={[
                { id: 1, type: 'favorite', description: 'New user registered', property: 'john@example.com', time: '2 hours ago' },
                { id: 2, type: 'edit', description: 'Property data updated', property: '150 properties', time: '5 hours ago' },
                { id: 3, type: 'analysis', description: 'System backup completed', property: 'Database snapshot', time: '1 day ago' },
                { id: 4, type: 'view', description: 'New subscription', property: 'Pro tier', time: '2 days ago' },
              ]}
            />
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <UserManagement />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <PropertyAnalytics />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <AIInsights />
      </TabPanel>
    </Container>
  );
};

export default EnhancedAdminDashboard;











